// The Lobby's « Profil » tab: the player's card as the others see it (on their own felt), then what they
// can change — the name shown at the table, the medal — and their account (the table has a tab of its own)
// (Google or guest, « Supprimer mon compte », which the stores ask for). PATCH /api/profile
// (backend/player_profile.py): rooms keep what a player had when they sat down.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, Trash2, UserRound } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { RankEmblem } from "../rank/RankEmblem";
import { RankBar, labelOf } from "../rank/rankParts";
import { useAuth } from "../../contexts/AuthContext";
import { AVATARS, avatarUrl } from "../../lib/avatars";
import { CALIBRATION_GAMES, rankInfo } from "../../lib/ranks";
import { matStyle, useMyTableTheme } from "../../lib/tableThemes";
import { t, trServer, useLang } from "../../lib/i18n";
import { NAME_MAX, foldName, nameError, nameLength } from "../../lib/playerName";

function Tile({ selected, onSelect, label, testId, children }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      title={label}
      onClick={onSelect}
      data-testid={testId}
      className={`relative aspect-square w-full rounded-full transition-transform duration-150 hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 ${
        selected ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-900" : "ring-1 ring-white/10 hover:ring-amber-400/40"
      }`}
    >
      {children}
      {selected && (
        <span className="absolute -bottom-0.5 -end-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-400 ring-2 ring-slate-900">
          <Check className="h-2.5 w-2.5 text-slate-900" strokeWidth={3} />
        </span>
      )}
    </button>
  );
}

function Section({ title, icon: Icon, lead, children, testId }) {
  return (
    <section className="rounded-2xl border border-white/[0.07] bg-slate-900/80 p-3 shadow-[0_6px_24px_rgba(0,0,0,0.3)]" data-testid={testId}>
      <h3 className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-amber-300/90">
        {lead}
        {Icon && <Icon className="h-4 w-4" />}
        {title}
      </h3>
      {children}
    </section>
  );
}

// The player's card as the others see it: their medal, name and rank, on their own felt.
function CallingCard({ name, picture, initial, rank }) {
  const themeId = useMyTableTheme();
  const info = rankInfo(rank);
  const r = rank || {};
  return (
    <div className="relative overflow-hidden rounded-2xl border border-amber-400/35 shadow-[0_14px_40px_rgba(0,0,0,0.55)]" data-testid="calling-card">
      <div className="rami-mat absolute inset-0" style={matStyle(themeId)} />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/55 to-slate-950/20 rtl:bg-gradient-to-l" />
      <span aria-hidden="true" className="absolute end-0 top-0 h-9 w-9 bg-gradient-to-bl from-amber-200 to-amber-600 [clip-path:polygon(0_0,100%_0,100%_100%)]" />
      <div className="relative flex items-center gap-3 px-3.5 py-2.5">
        <motion.div key={picture || "default"} initial={{ scale: 0.85, opacity: 0.6 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 420, damping: 22 }}>
          <Avatar className="h-14 w-14 ring-2 ring-amber-400/70 ring-offset-2 ring-offset-slate-950">
            {picture && <AvatarImage src={picture} alt="" />}
            <AvatarFallback className="bg-emerald-900 text-3xl text-amber-300">{initial}</AvatarFallback>
          </Avatar>
        </motion.div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/80">{t("profile.card")}</p>
          <p className="truncate text-lg leading-tight text-white" dir="auto" data-testid="calling-card-name">{name}</p>
          <div className="mt-1 flex items-center gap-2">
            <RankEmblem info={info} size={30} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-display text-sm italic text-amber-200">{labelOf(info)}</p>
              <RankBar info={info.calibrating ? { progress: info.games / CALIBRATION_GAMES } : info} className="mt-0.5" />
            </div>
          </div>
          <p className="mt-1.5 text-[11px] text-slate-300">
            {t("profile.record", { g: r.games ?? 0, w: r.wins ?? 0, l: r.losses ?? 0 })}
          </p>
        </div>
      </div>
    </div>
  );
}

export function ProfileTab({ rank }) {
  useLang();
  const { user, updateProfile, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user.name || "");
  const [avatar, setAvatar] = useState(user.avatar ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const clean = foldName(name);
  const nameChanged = clean !== user.name;
  const avatarChanged = avatar !== (user.avatar ?? null);
  // The server's rule, checked here first (lib/playerName.js).
  const problem = nameChanged ? nameError(name) : null;
  const canSave = (nameChanged || avatarChanged) && !problem && !busy;
  // The Google photo (a Google account before its first sign-in since the profile came shows the one it has).
  const googlePhoto = user.is_guest ? null : user.google_picture || (user.avatar ? null : user.picture);
  const preview = avatar ? avatarUrl(avatar) : googlePhoto;
  const initial = (clean || user.name || "?")[0]?.toUpperCase();

  const edit = (fn) => (...args) => { setSaved(false); setError(""); fn(...args); };

  const save = async (e) => {
    e?.preventDefault();
    if (!canSave) return;
    setBusy(true);
    setError("");
    const changes = {};
    if (nameChanged) changes.name = clean;
    if (avatarChanged) changes.avatar = avatar;
    try {
      const fresh = await updateProfile(changes);
      setName(fresh.name);
      setSaved(true);
    } catch (err) {
      setError(trServer(err?.response?.data?.detail) || t("profile.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    setDeleteError("");
    try {
      await deleteAccount();
      navigate("/");
    } catch (err) {
      setDeleting(false);
      setDeleteError(trServer(err?.response?.data?.detail) || t("account.deleteError"));
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-2" data-testid="profile-tab">
      <CallingCard name={clean || user.name} picture={preview} initial={initial} rank={rank} />

      <form onSubmit={save}>
        <Section title={t("profile.title")} icon={UserRound}>
          <label htmlFor="profile-name" className="block text-[11px] uppercase tracking-wider text-slate-400">
            {t("profile.name")}
          </label>
          {/* dir="auto": an Arabic name reads from the right even on a French screen. The counter stays
              out of the field, which may then run either way. */}
          <input
            id="profile-name"
            dir="auto"
            value={name}
            maxLength={NAME_MAX}
            autoComplete="nickname"
            onChange={edit((e) => setName(e.target.value))}
            data-testid="profile-name"
            className="mt-1 h-9 w-full rounded-xl border border-white/10 bg-slate-950/60 px-3.5 text-base text-white outline-none transition-colors focus:border-amber-400/60"
          />
          <div className={`mt-1.5 flex items-start justify-between gap-3 text-xs ${problem ? "text-rose-300" : "text-slate-500"}`}>
            <p data-testid="profile-name-hint">{problem || t("profile.nameHint")}</p>
            <span className="shrink-0 tabular-nums"><span dir="ltr">{nameLength(clean)}/{NAME_MAX}</span></span>
          </div>

          <p className="mt-3 text-[11px] uppercase tracking-wider text-slate-400">{t("profile.avatar")}</p>
          <div role="radiogroup" aria-label={t("profile.avatar")} className="mt-2 grid grid-cols-7 gap-2">
            <Tile
              selected={avatar === null}
              onSelect={edit(() => setAvatar(null))}
              label={googlePhoto ? t("profile.googlePhoto") : t("profile.initial")}
              testId="profile-avatar-default"
            >
              <Avatar className="h-full w-full">
                {googlePhoto && <AvatarImage src={googlePhoto} alt="" />}
                <AvatarFallback className="bg-emerald-900 text-lg text-amber-300">{initial}</AvatarFallback>
              </Avatar>
            </Tile>
            {AVATARS.map((id) => (
              <Tile key={id} selected={avatar === id} onSelect={edit(() => setAvatar(id))} label={t(`profile.avatars.${id}`)} testId={`profile-avatar-${id}`}>
                <img src={avatarUrl(id)} alt="" draggable={false} className="h-full w-full rounded-full" />
              </Tile>
            ))}
          </div>

          {error && <p className="mt-4 text-sm text-rose-300" role="alert" data-testid="profile-error">{error}</p>}

          <button
            type="submit"
            disabled={!canSave}
            data-testid="profile-save"
            className={`mt-3 flex h-9 w-full items-center justify-center gap-2 rounded-xl text-sm transition-colors disabled:cursor-not-allowed ${
              saved && !canSave ? "bg-emerald-600/20 text-emerald-300" : "bg-amber-400 text-slate-900 hover:bg-amber-300 disabled:opacity-40"
            }`}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {saved && !canSave ? t("profile.saved") : t("profile.save")}
          </button>
        </Section>
      </form>

      <Section title={t("profile.account")}>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="min-w-0 text-xs text-slate-400">
            <p className="truncate">
              {user.is_guest ? t("profile.guest") : `${t("profile.google")} · `}
              {!user.is_guest && <span dir="ltr">{user.email}</span>}
            </p>
            {user.is_guest && t("profile.guestNote") && <p className="mt-0.5 text-slate-500">{t("profile.guestNote")}</p>}
          </div>
          <button
            type="button"
            onClick={() => { setDeleteError(""); setConfirm(true); }}
            data-testid="account-delete"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs text-rose-300 transition-colors hover:bg-rose-500/10 hover:text-rose-200"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {t("account.delete")}
          </button>
        </div>
      </Section>

      <Dialog open={confirm} onOpenChange={(o) => !deleting && setConfirm(o)}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-sm p-5" data-testid="account-delete-dialog">
          <DialogTitle className="font-display text-lg text-rose-200">{t("account.deleteTitle")}</DialogTitle>
          <DialogDescription className="text-[13px] leading-relaxed text-slate-300">{t("account.deleteBody")}</DialogDescription>
          {deleteError && <p className="text-[12px] text-rose-300" role="alert">{deleteError}</p>}
          <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setConfirm(false)}
              disabled={deleting}
              className="h-10 rounded-xl border border-white/10 px-4 text-[13px] text-slate-200 hover:bg-white/5 disabled:opacity-50"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={deleting}
              data-testid="account-delete-confirm"
              className="h-10 rounded-xl bg-rose-600 px-4 text-[13px] text-white hover:bg-rose-500 disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
              {t("account.deleteConfirm")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProfileTab;
